import {AuthProfile, Wish, Wishlist} from "@/api/types.ts";
import {Button} from "@telegram-apps/telegram-ui";
import {TbGift, TbGiftOff, TbLock} from "react-icons/tb";
import {useState} from "react";
import {useWishlist} from "@/providers/WishlistProvider.tsx";
import {bookWish, unbookWish} from "@/api/apiMethods.ts";
import {useNavigation} from "@/providers/NavigationProvider.tsx";
import {useTranslation} from "react-i18next";
import {useAuthProfile} from "@/providers/AuthProfileProvider.tsx";
import {haptic} from "@/helpers/haptic.ts";

export const BookButton = (
  { wish, onUpdate, refreshWishlist, onCloseWish }:
  { wish: Wish, onUpdate: (wish: Wish) => void, refreshWishlist?: () => Promise<void>, onCloseWish?: () => void }
) => {
  const { showSnackbar } = useNavigation();
  const { t } = useTranslation();
  const [ isBookingLoading, setIsBookingLoading ] = useState<boolean>(false);

  let authProfileFromContext: AuthProfile | undefined = undefined;
  try {
    const { authProfile } = useAuthProfile();
    authProfileFromContext = authProfile
  } catch (e) { /* empty */ }

  let currentWishlist: Wishlist | undefined = undefined;
  try {
    const { wishlist } = useWishlist();
    currentWishlist = wishlist;
  } catch (e) {
    return null;
  }

  if (!currentWishlist) {
    throw new Error("BookButton must be inside the WishlistLayout");
  }

  const hideBookingState = wish.userId === authProfileFromContext?.user.id && authProfileFromContext?.user.showBookings === 'none';
  const canCancelOtherBooking = wish.userId === authProfileFromContext?.user.id && wish.bookedBy === 'otherUser' && !hideBookingState;

  const toggleBook = async () => {
    haptic.impact('medium');
    setIsBookingLoading(true);
    try {
      if (wish.bookedBy === 'currentUser') {
        await unbookWish(currentWishlist.id, wish.id);
        onUpdate({...wish, bookedBy: null});
        haptic.notification('success');
      } else if (canCancelOtherBooking) {
        haptic.notification('warning');
        const isConfirmed = confirm(t('wish.dialog.confirmCancelOtherBooking'));
        if (isConfirmed) {
          await unbookWish(currentWishlist.id, wish.id);
          onUpdate({...wish, bookedBy: null, bookedByUser: null});
          haptic.notification('success');
        }
      } else if (hideBookingState) {
        haptic.notification('warning');
        const isConfirmed = confirm(t('wish.dialog.confirmHiddenBooking'));
        if (isConfirmed) {
          await unbookWish(currentWishlist.id, wish.id);
          await bookWish(currentWishlist.id, wish.id);
          onUpdate({...wish, bookedBy: 'currentUser'});
          haptic.notification('success');
        }
      } else if (!wish.bookedBy) {
        await bookWish(currentWishlist.id, wish.id);
        onUpdate({...wish, bookedBy: 'currentUser'});
        haptic.notification('success');
      }
    } catch (e) {
      haptic.notification('error');
      // @ts-expect-error it works
      if (e?.response?.status === 403 || e?.response?.status === 404) {
        showSnackbar('❌', t('wish.alreadyBookedMessage'));
      } else {
        showSnackbar("❌", "system.unknowError");
      }
      if (refreshWishlist) await refreshWishlist();
      if (onCloseWish) onCloseWish();
    }
    setIsBookingLoading(false);
  }

  return (<>
    {(hideBookingState && wish.bookedBy !== 'currentUser' || !wish.bookedBy) &&
      <Button before={<TbGift size={25}/>}
              loading={isBookingLoading}
              disabled={isBookingLoading}
              onClick={toggleBook}
              stretched={true}
              mode={"filled"}>
        {t('wish.action.book')}
      </Button>}

    {wish.bookedBy === 'currentUser' &&
      <Button before={<TbGiftOff size={25}/>}
              loading={isBookingLoading}
              disabled={isBookingLoading}
              onClick={toggleBook}
              stretched={true}
              mode={"gray"}>
      <div>{t('wish.action.cancelBooking')}</div>
    </Button>}

    {!hideBookingState && wish.bookedBy === 'otherUser' &&
      <Button before={<TbLock size={25}/>}
              loading={canCancelOtherBooking && isBookingLoading}
              stretched={true}
              mode={"gray"}
              disabled={!canCancelOtherBooking || isBookingLoading}
              onClick={canCancelOtherBooking ? toggleBook : undefined}>
      <div>{t('wish.alreadyBooked')}</div>
    </Button>}
  </>)
};
